import { motion } from "framer-motion";
import { PageHeader } from "../components/PageHeader";
import { Process } from "../components/Process";
import { ContactCTA } from "../components/ContactCTA";
import { Reveal } from "../components/motion";
import { SectionLabel } from "../components/SectionLabel";
import { asList, mediaUrl, useFetch } from "../lib/api";
import { ease, pad } from "../lib/content";

const ServiceRow = ({ s, i }) => (
  <motion.article
    data-testid={`service-row-${i}`}
    className="group grid gap-6 border-t border-teal/15 py-12 transition-colors duration-500 lg:grid-cols-12 lg:gap-8"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease }}
  >
    <span className="font-mono text-sm text-moss lg:col-span-1">{pad(i + 1)}</span>
    <div className="lg:col-span-5">
      <h3 className="font-display text-3xl font-normal leading-tight tracking-tight text-teal sm:text-4xl">{s.title}</h3>
      <p className="mt-4 text-[15px] leading-relaxed text-teal-ink/85">{s.summary}</p>
    </div>
    <div className="lg:col-span-4">
      {s.description && <p className="text-[15px] leading-relaxed text-teal-ink/80">{s.description}</p>}
      {(s.deliverables || []).length > 0 && (
        <ul className="mt-5 space-y-2">
          {s.deliverables.map((d) => (
            <li key={d} className="flex items-start gap-3 text-sm text-teal">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
              {d}
            </li>
          ))}
        </ul>
      )}
    </div>
    <div className="lg:col-span-2">
      <div className={`aspect-square w-32 overflow-hidden sm:w-40 lg:ml-auto lg:w-full ${i % 2 ? "rounded-full" : "arch aspect-[3/4]"}`}>
        {s.image && <img src={mediaUrl(s.image)} alt={s.title} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />}
      </div>
    </div>
  </motion.article>
);

export default function Consulting() {
  const { data } = useFetch("/services");
  const services = asList(data);
  return (
    <main data-testid="consulting-page">
      <PageHeader
        testId="consulting-header"
        eyebrow="Consulting"
        lines={["Science-based advice,", "built for the field."]}
        intro="Technical consultancy, applied R&D, field experimentation and knowledge transfer for growers, industry organisations and commercial partners."
      />
      <section className="bg-paper py-20 sm:py-28">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <SectionLabel index="01" className="text-moss">Areas of expertise</SectionLabel>
          <div className="mt-12 border-b border-teal/15">
            {services.map((s, i) => (
              <ServiceRow key={s.id} s={s} i={i} />
            ))}
          </div>
        </div>
      </section>
      <Process index="02" />
      <section className="bg-mist py-20 sm:py-28">
        <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-4">
            <div className="arch aspect-[3/4] max-w-sm overflow-hidden bg-teal">
              <img src="/images/bruno-portrait.jpg" alt="Bruno Gatimel, Managing Director of Bio Smart Solutions" data-testid="consultant-photo" className="h-full w-full object-cover object-top" />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7 lg:col-start-6" delay={0.1}>
            <SectionLabel index="03" className="text-moss">Who you'll work with</SectionLabel>
            <h2 data-testid="consultant-name" className="mt-8 font-display text-4xl font-normal tracking-tight text-teal sm:text-5xl">Bruno Gatimel</h2>
            <p className="mt-2 font-display text-xl font-light text-moss">Managing Director</p>
            <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-teal-ink/85">
              Bio Smart Solutions combines agroecological expertise, practical crop protection experience and applied innovation, supporting projects from initial needs assessment and strategy development through to field implementation.
            </p>
          </Reveal>
        </div>
      </section>
      <ContactCTA />
    </main>
  );
}
